import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { baseUrl, config, requestID } from "../../../common/utils";
import {
  Box, Card, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Typography, IconButton, Stack,
  CircularProgress, Dialog, DialogContent, DialogTitle,
  Divider, Grid, Button, Paper, TextField, Container, InputAdornment
} from '@mui/material';
import {
  Visibility as EyeIcon,
  Close as CloseIcon,
  Print as PrintIcon,
  Person as PersonIcon,
  AssignmentInd as ReviewerIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon
} from '@mui/icons-material';

// --- Interface ---
interface GRNLog {
  id: number;
  request_id: string; 
  stock_name: string;
  ecid_item: string;
  quantity: number;
  date_time: string;
  to_location: string;
  reviewer_name: string;
  signer_out_name: string;
}

const GRNManager: React.FC = () => {
  const [logs, setLogs] = useState<GRNLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedGRN, setSelectedGRN] = useState<GRNLog | null>(null);
  
  // Filtering & Search States
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const fetchGRNData = async () => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("requestID", requestID()); 

      const logRes = await axios.post(`${baseUrl()}Geacloud_Inventorys/getGRNLogs`, formData, config);
      
      if (logRes.data && logRes.data.status === true) {
          setLogs(Array.isArray(logRes.data.data) ? logRes.data.data : []);
      }
    } catch (err) {
      console.error("Fetch error", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchGRNData(); }, []);

  // Filter & Search Logic
  const filteredLogs = (logs || []).filter(log => {
    // 1. Date Filter
    let matchesDate = true;
    if (startDate && endDate) {
        const logDate = new Date(log.date_time).toISOString().split('T')[0];
        matchesDate = logDate >= startDate && logDate <= endDate;
    }

    // 2. Search Filter (Stock Name or ID)
    const matchesSearch = log.stock_name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          log.request_id.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesDate && matchesSearch;
  });

  return (
    <Box sx={{ backgroundColor: '#f5f7fb', height: '100vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      
      {/* --- MATURE HEADER (Matching Inventory Control) --- */}
      <Box sx={{ background: 'linear-gradient(to right, #1e3a8a, #2563eb)', px: 3, py: 4, boxShadow: '0 4px 12px rgba(37,99,235,0.2)', zIndex: 10 }}>
        <Container maxWidth="xl">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h4" fontWeight={700} sx={{ color: 'white' }}>Goods Received Notes</Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', mt: 0.5 }}>Real-time GRN logs and verification tracking.</Typography>
              </Box>
              
              {/* Date Filters & Refresh Inside Header */}
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                <Stack direction="row" spacing={1} sx={{ bgcolor: 'rgba(255,255,255,0.1)', p: 0.8, borderRadius: 2 }}>
                    <TextField
                    type="date"
                    size="small"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    sx={{ width: 140, '& .MuiInputBase-input': { color: 'white', fontSize: '0.75rem' }, '& .MuiOutlinedInput-notchedOutline': { border: 'none' } }}
                    />
                    <Typography sx={{ color: 'white', alignSelf: 'center', opacity: 0.5 }}>-</Typography>
                    <TextField
                    type="date"
                    size="small"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    sx={{ width: 140, '& .MuiInputBase-input': { color: 'white', fontSize: '0.75rem' }, '& .MuiOutlinedInput-notchedOutline': { border: 'none' } }}
                    />
                </Stack>
                <IconButton onClick={fetchGRNData} disabled={loading} sx={{ bgcolor: 'white', color: '#1e3a8a', '&:hover': { bgcolor: '#f1f5f9' } }}>
                    <RefreshIcon fontSize="small" className={loading ? 'animate-spin' : ''} />
                </IconButton>
              </Box>
          </Box>
        </Container>
      </Box>
      
      {/* --- CONTENT AREA --- */}
      <Container maxWidth="xl" sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', py: 3 }}>
        
        {/* Search Bar Row */}
        <Box sx={{ mb: 2 }}>
            <TextField 
                fullWidth
                variant="outlined"
                placeholder="Search by Item Name ..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon color="primary" />
                        </InputAdornment>
                    ),
                    sx: { borderRadius: 3, bgcolor: 'white', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }
                }}
            />
        </Box>

        <Card sx={{ borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.05)', flexGrow: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <TableContainer sx={{ flexGrow: 1 }}>
            <Table stickyHeader size="small">
                <TableHead>
                <TableRow>
                    <TableCell sx={{ fontWeight: 700, bgcolor: '#f8fafc', py: 2 }}>S/N</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: '#f8fafc' }}>ITEM DESCRIPTION</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: '#f8fafc' }}>DESTINATION</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, bgcolor: '#f8fafc' }}>QTY</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: '#f8fafc' }}>RECEIVED BY</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: '#f8fafc' }}>VERIFIED BY</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: '#f8fafc' }}>DATE / TIME</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, bgcolor: '#f8fafc' }}>ACTION</TableCell>
                </TableRow>
                </TableHead>
                <TableBody>
                {loading ? (
                    <TableRow><TableCell colSpan={8} align="center" sx={{ py: 10 }}><CircularProgress /></TableCell></TableRow>
                ) : filteredLogs.length === 0 ? (
                    <TableRow><TableCell colSpan={8} align="center" sx={{ py: 10 }}>
                        <Typography color="text.secondary">No matching records found.</Typography>
                    </TableCell></TableRow>
                ) : filteredLogs.map((log, index) => (
                    <TableRow key={log.id} hover>
                    <TableCell sx={{ color: 'text.secondary', fontWeight: 600 }}>{index + 1}</TableCell>
                    <TableCell>
                        <Typography variant="body2" fontWeight={700} color="primary.dark">{log.stock_name}</Typography>
                        <Typography variant="caption" color="text.secondary">{log.ecid_item}</Typography>
                    </TableCell>
                    <TableCell><Typography variant="body2">{log.to_location}</Typography></TableCell>
                    <TableCell align="right">
                        <Typography variant="body2" sx={{ fontWeight: 800, color: '#059669' }}>{log.quantity}</Typography>
                    </TableCell>
                    <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <PersonIcon sx={{ fontSize: 16, color: '#3b82f6' }} />
                            <Typography variant="body2">{log.signer_out_name || '---'}</Typography>
                        </Stack>
                    </TableCell>
                    <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <ReviewerIcon sx={{ fontSize: 16, color: '#6366f1' }} />
                            <Typography variant="body2">{log.reviewer_name || '---'}</Typography>
                        </Stack>
                    </TableCell>
                    <TableCell>
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                            {new Date(log.date_time).toLocaleDateString()}<br/>
                            {new Date(log.date_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </Typography>
                    </TableCell>
                    <TableCell align="center">
                        <IconButton size="small" onClick={() => setSelectedGRN(log)} sx={{ bgcolor: '#eff6ff', color: '#1e3a8a' }}>
                        <EyeIcon fontSize="small" />
                        </IconButton>
                    </TableCell>
                    </TableRow>
                ))}
                </TableBody>
            </Table>
            </TableContainer>
        </Card>
      </Container>

      {/* VOUCHER DIALOG */}
      <Dialog open={!!selectedGRN} onClose={() => setSelectedGRN(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ borderBottom: '1px solid #eee' }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight={800}>GRN Voucher</Typography>
            <IconButton onClick={() => setSelectedGRN(null)} size="small"><CloseIcon /></IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {selectedGRN && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">ITEM</Typography>
                    <Typography variant="h6" fontWeight={800} color="primary">{selectedGRN.stock_name}</Typography>
                </Grid>
                <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">QUANTITY</Typography>
                    <Typography variant="body1" fontWeight={700}>{selectedGRN.quantity}</Typography>
                </Grid>
                <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">LOCATION</Typography>
                    <Typography variant="body1" fontWeight={700}>{selectedGRN.to_location}</Typography>
                </Grid>
                <Grid item xs={12}><Divider /></Grid>
                <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">RECEIVED BY</Typography>
                    <Typography variant="body2" fontWeight={600}>{selectedGRN.signer_out_name}</Typography>
                </Grid>
                <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">VERIFIED BY</Typography>
                    <Typography variant="body2" fontWeight={600}>{selectedGRN.reviewer_name}</Typography>
                </Grid>
                <Grid item xs={12}>
                    <Button fullWidth variant="contained" startIcon={<PrintIcon />} sx={{ mt: 2, bgcolor: '#1e3a8a' }}>Print Copy</Button>
                </Grid>
            </Grid>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default GRNManager;